import {
  Conversation,
  Member,
  MemberAlias,
  MembersData,
  MessageAction,
  User,
} from '@nextforce/api'
import { Divider, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Keyboard, SectionList, View } from 'react-native'
import { Menu } from 'react-native-popup-menu'
import { SafeAreaView } from 'react-native-safe-area-context'
import { confirmationAlert } from 'shared/alerts'
import ActionSheetMenu from 'shared/components/chat/action_sheet_menu'
import { RoundedButton } from 'shared/components/rounded-button'
import { filterMembers } from '../recipients/util'
import { MemberCard } from './card'
import { getAliasDisplayName } from './member_alias'
import { RecipientSearchBar } from './search_bar'
interface Props {
  conversation: Conversation | undefined
  user: User | undefined
  nonAliasMembers: MembersData | undefined
  aliasMembers: MemberAlias[] | undefined
  search?: boolean
  onRemoveMember(member: Member): void
  onSendDirectMessage(memberId: string): void
  onToggleAdminStatus(member: Member): void
  onRemoveMemberAlias(alias: MemberAlias): void
}

type Section = {
  title: string
  data: MemberListItem[]
  type: 'addedManually' | 'aliases'
}

type MemberListItem =
  | (Member & { type: 'member' })
  | { name: MemberAlias; type: 'alias' }

export function ChatGroupMembersView({
  conversation,
  user,
  nonAliasMembers,
  aliasMembers,
  search,
  onRemoveMember,
  onSendDirectMessage,
  onToggleAdminStatus,
  onRemoveMemberAlias,
}: Props) {
  const styles = useStyles()
  const [searchText, setSearchText] = useState('')
  const [selectedMember, setSelectedMember] = useState<Member | null>(null)
  const menuRef = useRef<Menu | null>(null)

  const filteredMembers = filterMembers(nonAliasMembers, searchText)

  const sections = useMemo(() => {
    const items: Section[] = []
    if (aliasMembers && aliasMembers.length > 0 && searchText.trim() === '') {
      const aliasData: MemberListItem[] = aliasMembers.map(alias => ({
        name: alias,
        type: 'alias',
      }))
      if (aliasData.length) {
        items.push({
          title: t('activeAliases'),
          data: aliasData,
          type: 'aliases',
        })
      }
    }

    if (filteredMembers && filteredMembers.length > 0) {
      const nonAliasMemberData: MemberListItem[] = filteredMembers.map(m => ({
        ...m,
        type: 'member',
      }))

      if (nonAliasMemberData.length > 0) {
        items.push({
          title: t('otherMembers'),
          data: nonAliasMemberData,
          type: 'addedManually',
        })
      }
    }

    return items
  }, [aliasMembers, filteredMembers, searchText])

  interface MenuItem {
    name: string
    title: string
  }

  const handleMemberAction = useCallback(
    (action: MessageAction) => {
      if (!selectedMember) return

      const actionName = action.name as
        | 'removeMember'
        | 'sendDirectMessage'
        | 'toggleGroupAdmin'

      switch (actionName) {
        case 'removeMember':
          confirmationAlert(t('removeMemberConfirm'))
            .then(value => {
              if (value) {
                onRemoveMember(selectedMember)
              }
            })
            .catch(e => null)
          break
        case 'sendDirectMessage':
          onSendDirectMessage(selectedMember.id)
          break
        case 'toggleGroupAdmin':
          onToggleAdminStatus(selectedMember)
          break
        default:
          break
      }
    },
    [selectedMember, onRemoveMember, onSendDirectMessage, onToggleAdminStatus]
  )

  const getMenuItems = useCallback(
    (member: Member) => {
      const items: MenuItem[] = [
        ...(conversation?.allowEdit
          ? [{ name: 'removeMember', title: t('removeMember') }]
          : []),
        ...(conversation?.messageActions.some(
          action => action.name === 'sendDirectMessage'
        )
          ? [{ name: 'sendDirectMessage', title: t('sendDirectMessage') }]
          : []),
        ...(conversation?.messageActions.some(
          action => action.name === 'assignAdmin'
        )
          ? [
              {
                name: 'toggleGroupAdmin',
                title: member.isAdmin
                  ? t('removeGroupAdmin')
                  : t('makeGroupAdmin'),
              },
            ]
          : []),
        { name: 'cancel', title: t('cancel') },
      ]
      return items
    },
    [conversation?.allowEdit, conversation?.messageActions]
  )
  const memberActions = useMemo((): MessageAction[] => {
    if (!selectedMember) {
      return []
    }
    // Convert our custom member actions into the format ActionSheetMenu expects
    return getMenuItems(selectedMember).map(item => ({
      name: item.name as any,
      title: item.title,
    }))
  }, [selectedMember, getMenuItems])

  const onMemberPress = useCallback(
    (member: Member) => {
      const menuItems = getMenuItems(member)
      if (menuItems.length === 1 || member.id === user?.id) {
        return
      }
      setSelectedMember(member)
    },
    [getMenuItems, user?.id]
  )

  const renderSectionHeader = useCallback(
    ({ section: { title, type } }: { section: Section }) => {
      if (!aliasMembers || aliasMembers.length === 0) {
        return null
      }
      return (
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionHeaderTitle}>{title}</Text>
        </View>
      )
    },
    [aliasMembers, styles.sectionHeader, styles.sectionHeaderTitle]
  )

  const renderItem = useCallback(
    ({ item }: { item: MemberListItem }) => {
      if (item.type === 'alias') {
        return (
          <View style={styles.aliasItemContainer}>
            <View style={styles.aliasNameContainer}>
              <Text>{getAliasDisplayName(item.name)}</Text>
            </View>
            {conversation?.allowEdit ? (
              <RoundedButton
                title={t('remove')}
                onPress={() => onRemoveMemberAlias(item.name)}
                danger
              />
            ) : null}
          </View>
        )
      }
      if (item.type === 'member') {
        return (
          <MemberCard member={item} showAdminStatus onPress={onMemberPress} />
        )
      }
      return null
    },
    [
      conversation?.allowEdit,
      onMemberPress,
      onRemoveMemberAlias,
      styles.aliasItemContainer,
      styles.aliasNameContainer,
    ]
  )

  useEffect(() => {
    if (selectedMember) {
      menuRef.current?.open().catch(e => null)
    }
  }, [selectedMember])

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <RecipientSearchBar
          searchText={searchText}
          setSearchText={setSearchText}
          autoFocus={search}
        />
        <SectionList
          sections={sections}
          renderSectionHeader={renderSectionHeader}
          renderItem={renderItem}
          ItemSeparatorComponent={() => <Divider />}
          contentContainerStyle={styles.contentContainer}
          onScrollBeginDrag={() => Keyboard.dismiss()}
        />
        <ActionSheetMenu
          menuRef={menuRef}
          actions={memberActions}
          onAction={handleMemberAction}
          onClose={() => setSelectedMember(null)}
        />
      </View>
    </SafeAreaView>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
  },
  sectionHeader: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.grey7,
    paddingVertical: 10,
    paddingHorizontal: 10,
    padding: 5,
  },
  sectionHeaderTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  sectionHeaderDescription: {
    fontSize: 14,
    color: theme.colors.grey1,
  },
  aliasItemContainer: {
    padding: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  aliasNameContainer: { flex: 1 },
}))
